import { RootState } from '@core/store/states/root.state'
import { createSelector } from '@ngrx/store'
import {
  hasNoRankingAvailableAtAll,
  hasPercentageRankingOnAccumulatedMode,
  hasRankingAvailableWhenIsMultiEntity,
  hasRankingAvailableWhenIsSingleEntity,
  resolveRankingColumns,
  resolveRankingHeader,
  resolveRankingRows
} from '@features/rankings/utils/ranking.utils'
import { RANKING_MATCH_PREVIOUS_STATES } from '@features/rankings/constants/rankings.constants'
import { RankingWarningMode } from '@features/rankings/enums/ranking.enums'
import { MetricFinderKey } from '@core/enums/metric.enums'
import { Match } from '@core/models/match.models'
import { ContextMenuHolderWrapper } from '@core/models/context-menu.models'
import {
  selectAggregationContextMetadata,
  selectAggregationsContext
} from '@features/aggregations/store/selectors/aggregations.selectors'
import { getAggregationAsSelectable } from '@features/aggregations/utils/aggregations.utils'
import { selectCurrentSeasonAndCompetition } from '@features/seasons/state/selectors/seasons.selectors'
import { CallingContext } from '@features/seasons/models/seasons.models'
import { MatchState } from '@mediacoach/ui'

export const selectRankingFeature = (state: RootState) => state.rankings

export const selectRanking = createSelector(selectRankingFeature, (state) => state.ranking)
export const selectRankingLoader = createSelector(selectRankingFeature, (state) => state.loading)
export const selectSourceMetric = createSelector(selectRankingFeature, (state) => state.sourceMetric)

export const selectMetricToRank = createSelector(selectRankingFeature, (state) => state.metricToRank)

export const selectRankingAggregation = createSelector(selectMetricToRank, (metricToRank) =>
  metricToRank ? metricToRank.aggregationDataSource : 'Accumulated'
)

export const selectRankingAggregationSelectable = createSelector(selectRankingAggregation, (aggregation) =>
  getAggregationAsSelectable(aggregation)
)

export const selectRankedMetricKey = createSelector(selectMetricToRank, (metricToRank) => metricToRank?.metricName)

export const selectRankedMetricContext = createSelector(selectRankingFeature, (state) => state.metricContext)

export const selectRankingMatchDay = createSelector(selectRankingFeature, (state) => state.matchDay)

export const selectRankedMetricDisplayMode = createSelector(selectRankingFeature, (state) => state.displayMode)

export const selectRankingHeader = (callingContext: CallingContext) =>
  createSelector(
    selectRankingMatchDay,
    selectRankedMetricDisplayMode,
    selectRankedMetricContext,
    selectRankedMetricKey,
    selectCurrentSeasonAndCompetition(callingContext),
    (matchDay, displayMode, metricContext, metricKey, { competition }) =>
      resolveRankingHeader(displayMode, { ...metricContext, competition }, metricKey, matchDay)
  )

export const selectRankingColumns = createSelector(
  selectRankedMetricDisplayMode,
  selectRankingAggregation,
  selectRankedMetricContext,
  (displayMode, mode, context) =>
    resolveRankingColumns(displayMode, (context as ContextMenuHolderWrapper)?.overrideAggregationMode ?? mode)
)

export const selectRankingRows = (callingContext: CallingContext) =>
  createSelector(
    selectRankedMetricDisplayMode,
    selectRanking,
    selectRankedMetricContext,
    selectCurrentSeasonAndCompetition(callingContext),
    (displayMode, rankedMetric, metricContext, { competition }) =>
      resolveRankingRows(displayMode, rankedMetric, { ...metricContext, competition })
  )

export const selectRankingWarning = createSelector(
  selectSourceMetric,
  selectRankedMetricContext,
  selectRanking,
  selectRankingAggregation,
  selectAggregationsContext,
  selectAggregationContextMetadata,
  (source, metricContext, ranking, aggregation, aggregationContext, { isVsEnabled }) => {
    if (hasPercentageRankingOnAccumulatedMode(source, aggregation)) {
      return RankingWarningMode.NoPercent
    } else if (
      hasNoRankingAvailableAtAll(source) ||
      !hasRankingAvailableWhenIsSingleEntity(isVsEnabled, aggregationContext, aggregation, source) ||
      !hasRankingAvailableWhenIsMultiEntity(isVsEnabled, aggregationContext, source)
    ) {
      return RankingWarningMode.NoRank
    } else if (
      !/previa/i.test(source?.keyPath) &&
      RANKING_MATCH_PREVIOUS_STATES.includes((metricContext as Match)?.state?.name as MatchState)
    ) {
      return RankingWarningMode.NoMatch
    }
  }
)

export const selectWarningI18n = createSelector(
  selectRankingAggregationSelectable,
  selectRankingWarning,
  (aggregation, mode) => {
    let text: string
    let param: string
    switch (mode) {
      case RankingWarningMode.NoMatch:
        text = 'MTR_RANKINGS_NO_MATCH_DATA'
        break
      case RankingWarningMode.NoRank:
        text = 'MTR_RANKINGS_NO_DATA'
        param = aggregation.text
        break
      case RankingWarningMode.NoPercent:
        text = 'MTR_RANKINGS_NO_DATA_PERC'
        param = aggregation.text
        break
    }
    return { mode, text, param }
  }
)

export const selectShowFooter = createSelector(
  selectAggregationsContext,
  (context) => context === MetricFinderKey.Player
)
